import { Link } from "react-router-dom";
import { CalendarDays, Star } from "lucide-react";
import { Reveal, Eyebrow, LineReveal } from "../components/motion";
import BookNowBox from "../components/BookNowBox";
import ReviewsWall from "../components/ReviewsWall";
import { LODGIFY_BOOK_NOW_SNIPPET, DOMES } from "../config/site";

function RatingBadge({ dome }) {
  if (!dome) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[0.56rem] uppercase tracking-widest2 text-lavglow shrink-0 whitespace-nowrap"
      data-testid={`rating-badge-${dome.id}`}
    >
      <Star size={11} className="fill-lavglow text-lavglow" />
      {dome.rating} · {dome.reviews} Airbnb reviews
    </span>
  );
}

export default function Book() {
  return (
    <>
      <section className="pt-36 pb-24 md:pt-44 md:pb-32 px-6 md:px-16" data-testid="book-page">
      <div className="max-w-5xl mx-auto">
        <Eyebrow className="mb-6">Availability & booking</Eyebrow>
        <LineReveal lines={["Book your stay."]} className="display-1 text-cream mb-4" />
        <Reveal delay={0.3}>
          <p className="text-sage max-w-xl leading-relaxed text-sm md:text-base">
            Pick your dates and guest count in the boxes below — your live total price
            appears instantly, and Book Now takes you straight to our secure Lodgify checkout.
          </p>
        </Reveal>

        <Reveal delay={0.35} className="mt-12">
          <div className="border border-white/10 rounded-3xl bg-moss/60 p-6 md:p-10" data-testid="booking-panel">
            <div className="flex items-center gap-3 mb-6">
              <CalendarDays size={18} className="text-lavglow" />
              <p className="font-mono text-[0.65rem] uppercase tracking-widest2 text-lavglow">
                Book directly here · Live pricing
              </p>
            </div>
            {LODGIFY_BOOK_NOW_SNIPPET ? (
              <div className="grid gap-8 md:gap-6 md:grid-cols-2">
                <div className="md:border md:border-white/10 md:rounded-2xl md:bg-moss/80 md:p-6">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="font-mono text-[0.58rem] uppercase tracking-widest2 text-fern">Dome 01 · Piwakawaka</p>
                    <RatingBadge dome={DOMES.find((d) => d.id === "piwakawaka")} />
                  </div>
                  <BookNowBox rental="piwakawaka" />
                </div>
                <div className="md:border md:border-white/10 md:rounded-2xl md:bg-moss/80 md:p-6">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="font-mono text-[0.58rem] uppercase tracking-widest2 text-fern">Dome 02 · Kārearea</p>
                    <RatingBadge dome={DOMES.find((d) => d.id === "karearea")} />
                  </div>
                  <BookNowBox rental="karearea" />
                </div>
              </div>
            ) : (
              <div data-testid="booking-widget-coming-soon">
                <p className="serif-italic text-2xl md:text-3xl text-sage max-w-xl leading-snug mb-4">
                  We're wiring up live booking right here on the page.
                </p>
                <p className="text-sage text-sm leading-relaxed max-w-lg mb-8">
                  Very soon you'll be able to check dates and book your stay without leaving this
                  page. Want to lock in a stay now? Send us an inquiry and we'll confirm your dates
                  personally.
                </p>
                <Link to="/contact" className="btn-primary inline-flex" data-testid="booking-inquiry-button">
                  Send an inquiry
                </Link>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.42}>
          <p className="text-fern text-xs leading-relaxed mt-6 max-w-lg">
            Booking for a wedding or event, or want to ask something first?{" "}
            <Link to="/contact" className="text-lavender hover:text-lavglow transition-colors link-underline" data-testid="book-contact-link">
              Send us an inquiry
            </Link>{" "}
            and we'll take care of you.
          </p>
        </Reveal>
      </div>
      </section>
      <ReviewsWall />
    </>
  );
}
